import React, { useState, useEffect } from "react";
import "../../styles/common/mcqViewer.scss";
import { parseJsonRobustly } from "../../helper/jsonUtils";
import {
  updatePageForCourse,
  updatePageForSubject,
  updatePageForTopic,
  updatePageForSubtopic,
  getPageForCourse,
  getPageForSubject,
  getPageForTopic,
  getPageForSubtopic,
} from "../../helper/apicalls/course";
import { updateInitialTest } from "../../helper/apicalls/initialTest";
import { DeleteIcon, AddCircleIcon, AddIcon, EditIcon } from "../../icons";
import GlobalLoader from "./GlobalLoader";
import DeleteConfirmPopUp from "./deleteConfirmPopUp";

const MCQViewer = ({
  questionData,
  onUpdate,
  pageId,
  pageData,
  context,
  courseKey,
  subjectKey,
  topicKey,
  subTopicKey,
  contentIndex,
  onAddNext,
  title = "Multiple Choice Question",
  startInEditMode = false,
  externalSaveSignal,
  externalCancelSignal,
  allowEditWithoutPersist = false,
  permission,
  provideUpdatedBuilder,
}) => {
  const [questions, setQuestions] = useState([]);
  const [editMode, setEditMode] = useState(!!startInEditMode);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const parseQuestionsFromData = (qd) => {
    const toLabeledOption = (text, idxOrKey) => {
      const label =
        typeof idxOrKey === "string"
          ? idxOrKey
          : String.fromCharCode(65 + (idxOrKey || 0));
      return { [label]: String(text ?? "") };
    };
    const normalizeOptions = (opts) => {
      if (opts && !Array.isArray(opts) && typeof opts === "object") {
        return Object.entries(opts).map(([key, val]) =>
          toLabeledOption(val, key),
        );
      }

      const arr = Array.isArray(opts) ? opts : [];
      return arr.map((opt, idx) => {
        if (opt && typeof opt === "object") {
          const keys = Object.keys(opt);
          if (keys.length === 1) {
            const key = keys[0];
            return { [key]: String(opt[key] ?? "") };
          }
          const val =
            opt.text ??
            opt.option ??
            opt.value ??
            opt.answer ??
            JSON.stringify(opt);
          return toLabeledOption(val, idx);
        }
        return toLabeledOption(opt ?? "", idx);
      });
    };
    if (!qd) return [];
    try {
      let parsedData = qd;
      if (typeof qd === "string") {
        parsedData = parseJsonRobustly(qd);
      }
      if (
        parsedData &&
        typeof parsedData === "object" &&
        parsedData.structured
      ) {
        parsedData = parsedData.structured;
      }
      if (Array.isArray(parsedData))
        return parsedData.map((q) => ({
          ...q,
          options: normalizeOptions(q?.options || q?.choices || []),
        }));
      if (
        parsedData &&
        typeof parsedData === "object" &&
        parsedData.questions
      ) {
        return (parsedData.questions || []).map((q) => ({
          ...q,
          options: normalizeOptions(q?.options || q?.choices || []),
        }));
      }
      if (parsedData && typeof parsedData === "object")
        return [
          {
            ...parsedData,
            options: normalizeOptions(
              parsedData?.options || parsedData?.choices || [],
            ),
          },
        ];
      return [];
    } catch (error) {
      console.error("Error parsing MCQ data:", error);
      return [
        {
          question: typeof qd === "string" ? qd : JSON.stringify(qd, null, 2),
          options: [],
        },
      ];
    }
  };

  useEffect(() => {
    setQuestions(parseQuestionsFromData(questionData));
  }, [questionData]);

  useEffect(() => {
    if (!externalSaveSignal) return;
    if (typeof onUpdate !== "function") return;
    try {
      const tempOriginal = { type: "mcq", content: "{}" };
      const updatedItem = buildUpdatedItemFromOriginal(tempOriginal);
      onUpdate({ content: [updatedItem] });
    } catch (e) {
      console.error("Error updating MCQ data:", e);
    }
  }, [externalSaveSignal]);

  useEffect(() => {
    if (!externalCancelSignal) return;
    setQuestions((prev) => prev);
    setEditMode(!!startInEditMode);
  }, [externalCancelSignal]);

  const buildUpdatedItemFromOriginal = (originalItem) => {
    let out;
    if (typeof originalItem === "string") {
      try {
        out = JSON.parse(originalItem);
      } catch (e) {
        out = {};
      }
    } else {
      const cloned = JSON.parse(JSON.stringify(originalItem || {}));
      out = cloned && typeof cloned === "object" ? cloned : {};
    }

    if (out.content && typeof out.content === "string") {
      try {
        out.content = JSON.parse(out.content);
      } catch (e) {
        out.content = {};
      }
    }

    let originalQuestions = [];
    if (out && out.content && Array.isArray(out.content.questions)) {
      originalQuestions = out.content.questions;
    } else if (Array.isArray(out.questions)) {
      originalQuestions = out.questions;
      if (!out.content) out.content = {};
      out.content.questions = originalQuestions;
    } else {
      out.type = out.type || "mcq";
      out.content = out.content || {};
      out.content.questions = [];
      originalQuestions = out.content.questions;
    }

    const optDisplay = (opt) => {
      if (typeof opt === "string") return opt;
      if (opt && typeof opt === "object") {
        if (Object.keys(opt).length === 1) {
          const v = Object.values(opt)[0];
          return typeof v === "string" ? v : JSON.stringify(v);
        }
        return (
          opt.text ||
          opt.option ||
          opt.value ||
          opt.answer ||
          JSON.stringify(opt)
        );
      }
      return String(opt ?? "");
    };

    const toLabeledOption = (text, idx) => ({
      [String.fromCharCode(65 + idx)]: String(text ?? ""),
    });

    let updated;
    if (originalQuestions.length === 0 && questions.length > 0) {
      updated = questions.map((q) => {
        const questionObj = {
          question: q.question || "",
          options: Array.isArray(q.options)
            ? q.options.map((opt, idx) => toLabeledOption(optDisplay(opt), idx))
            : [],
          answer: q.answer || "",
        };

        if (q.explanation !== undefined) {
          questionObj.explanation = q.explanation;
        }

        return questionObj;
      });
    } else {
      updated = (originalQuestions || []).map((oq, i) => {
        const eq = questions[i] || {};
        const newQ = JSON.parse(JSON.stringify(oq));

        const originalQText = oq.question || oq.title || oq.text || "";
        if ((eq.question || "") !== originalQText) {
          if ("question" in newQ) newQ.question = eq.question || "";
          else if ("title" in newQ) newQ.title = eq.question || "";
          else if ("text" in newQ) newQ.text = eq.question || "";
          else newQ.question = eq.question || "";
        }
        const oOpts = oq.options || oq.choices || [];
        const eOpts = eq.options || eq.choices || [];
        const updatedOpts = Array.from({ length: eOpts.length }, (_, j) => {
          const editedOpt = eOpts[j];
          let editedText = "";
          if (typeof editedOpt === "string") {
            editedText = editedOpt;
          } else if (editedOpt && typeof editedOpt === "object") {
            editedText = optDisplay(editedOpt);
          } else {
            const origOpt = oOpts[j];
            editedText = origOpt !== undefined ? optDisplay(origOpt) : "";
          }
          return toLabeledOption(editedText, j);
        });

        if ("options" in newQ)
          newQ.options = updatedOpts.map((o, j) =>
            toLabeledOption(optDisplay(o), j),
          );
        else if ("choices" in newQ)
          newQ.choices = updatedOpts.map((o, j) =>
            toLabeledOption(optDisplay(o), j),
          );
        else
          newQ.options = updatedOpts.map((o, j) =>
            toLabeledOption(optDisplay(o), j),
          );

        const resolvedOptsTexts = updatedOpts.map(optDisplay);
        const chosenIdx =
          typeof eq.correctIndex === "number"
            ? Math.min(
                Math.max(eq.correctIndex, 0),
                Math.max(resolvedOptsTexts.length - 1, 0),
              )
            : Math.max(
                0,
                resolvedOptsTexts.findIndex(
                  (t) => t === (eq.answer || eq.correct || ""),
                ),
              );
        const newAnswer = resolvedOptsTexts[chosenIdx] || "";
        if ("answer" in newQ) newQ.answer = newAnswer;
        else if ("correct" in newQ) newQ.correct = newAnswer;
        else newQ.answer = newAnswer;

        if (eq.explanation !== undefined && "explanation" in newQ) {
          newQ.explanation = eq.explanation;
        } else if (eq.explanation !== undefined) {
          newQ.explanation = eq.explanation;
        }

        return newQ;
      });
    }

    out.type = "mcq";
    out.content = out.content || {};
    out.content.questions = updated;
    out.content = JSON.stringify(out.content);
    return out;
  };

  useEffect(() => {
    if (typeof provideUpdatedBuilder === "function") {
      provideUpdatedBuilder((originalItem) =>
        buildUpdatedItemFromOriginal(originalItem),
      );
    }
  }, [provideUpdatedBuilder, questions]);

  const doUpdate = async () => {
    if (!pageId || !context) return;
    let contentArray = [];
    try {
      let rawContent = pageData?.content;
      if (typeof rawContent === "string") {
        const parsed = JSON.parse(rawContent);
        if (Array.isArray(parsed)) contentArray = parsed;
        else if (parsed && typeof parsed === "object") contentArray = [parsed];
      } else if (Array.isArray(rawContent)) {
        contentArray = rawContent;
      } else if (rawContent && typeof rawContent === "object") {
        contentArray = [rawContent];
      }
    } catch (e) {
      return;
    }

    if (!Array.isArray(contentArray)) return;
    if (
      typeof contentIndex !== "number" ||
      contentIndex < 0 ||
      contentIndex >= contentArray.length
    )
      return;

    const updatedArray = [...contentArray];

    const originalItem = updatedArray[contentIndex];
    const updatedItem = buildUpdatedItemFromOriginal(originalItem);
    updatedArray[contentIndex] = updatedItem;
    const payload = { id: pageId, content: updatedArray };
    let updated;

    setLoading(true);
    try {
      switch (context) {
        case "course":
          await updatePageForCourse(payload);
          updated = await getPageForCourse(courseKey, pageId);
          break;
        case "subject":
          await updatePageForSubject(payload);
          updated = await getPageForSubject(subjectKey, pageId);
          break;
        case "topic":
          await updatePageForTopic(payload);
          updated = await getPageForTopic(topicKey, pageId);
          break;
        case "subtopic":
          await updatePageForSubtopic(payload);
          updated = await getPageForSubtopic(subTopicKey, pageId);
          break;
        case "initial-test": {
          const testPayload = { content: updatedArray };
          const resp = await updateInitialTest(pageId, testPayload);
          if (resp && Array.isArray(resp.content)) {
            updated = resp;
          } else {
            updated = {
              ...(pageData || {}),
              id: pageId,
              content: updatedArray,
            };
          }
          break;
        }
        default:
          updated = payload;
      }

      if (onUpdate) onUpdate(updated);
      setEditMode(false);
    } finally {
      setLoading(false);
    }
  };

  const doDelete = async () => {
    if (!pageId || !context) return;
    let contentArray = [];
    try {
      let rawContent = pageData?.content;
      if (typeof rawContent === "string") {
        const parsed = JSON.parse(rawContent);
        if (Array.isArray(parsed)) contentArray = parsed;
        else if (parsed && typeof parsed === "object") contentArray = [parsed];
      } else if (Array.isArray(rawContent)) {
        contentArray = rawContent;
      } else if (rawContent && typeof rawContent === "object") {
        contentArray = [rawContent];
      }
    } catch (e) {
      return;
    }

    if (!Array.isArray(contentArray)) return;
    if (
      typeof contentIndex !== "number" ||
      contentIndex < 0 ||
      contentIndex >= contentArray.length
    )
      return;

    const updatedArray = contentArray.filter((_, idx) => idx !== contentIndex);

    const payload = { id: pageId, content: updatedArray };
    let updated;

    setLoading(true);
    try {
      switch (context) {
        case "course":
          await updatePageForCourse(payload);
          updated = await getPageForCourse(courseKey, pageId);
          break;
        case "subject":
          await updatePageForSubject(payload);
          updated = await getPageForSubject(subjectKey, pageId);
          break;
        case "topic":
          await updatePageForTopic(payload);
          updated = await getPageForTopic(topicKey, pageId);
          break;
        case "subtopic":
          await updatePageForSubtopic(payload);
          updated = await getPageForSubtopic(subTopicKey, pageId);
          break;
        case "initial-test": {
          const testPayload = { content: updatedArray };
          const resp = await updateInitialTest(pageId, testPayload);
          if (resp && Array.isArray(resp.content)) {
            updated = resp;
          } else {
            updated = {
              ...(pageData || {}),
              id: pageId,
              content: updatedArray,
            };
          }
          break;
        }
        default:
          updated = payload;
      }

      if (onUpdate) onUpdate(updated);
      setIsDeleteConfirmOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const Button = ({
    children,
    onClick,
    variant,
    size,
    className,
    disabled,
  }) => {
    const baseClass = "mcq-button";
    const variantClass = variant ? `mcq-button--${variant}` : "";
    const sizeClass = size ? `mcq-button--${size}` : "";
    const disabledClass = disabled ? "mcq-button--disabled" : "";
    const classes = `${baseClass} ${variantClass} ${sizeClass} ${disabledClass} ${
      className || ""
    }`;

    return (
      <button className={classes} onClick={onClick} disabled={disabled}>
        {children}
      </button>
    );
  };

  const Radio = ({ checked, onChange, className }) => {
    return (
      <input
        type="radio"
        className={`mcq-radio ${className || ""}`}
        checked={checked}
        onChange={onChange}
      />
    );
  };

  const IconButton = ({ children, onClick, size, className }) => {
    const sizeClass = size ? `mcq-icon-button--${size}` : "";
    const classes = `mcq-icon-button ${sizeClass} ${className || ""}`;

    return (
      <button className={classes} onClick={onClick}>
        {children}
      </button>
    );
  };

  const AddCircleOutlineIcon = () => (
    <AddCircleIcon className="mcq-icon mcq-icon--add" />
  );

  const DeleteOutlineIcon = () => (
    <DeleteIcon className="mcq-icon mcq-icon--delete" />
  );

  const handleCloseMCQ = () => {
    const restored = parseQuestionsFromData(questionData);
    setQuestions(restored);
    setEditMode(false);
  };

  return (
    <div className={`mcq-viewer mcq-viewer--${editMode ? "edit" : "view"}`}>
      <GlobalLoader visible={loading} text="Processing..." />
      {isDeleteConfirmOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isDeleteConfirmOpen}
          setIsConfirmDeleteOpen={setIsDeleteConfirmOpen}
          handleDelete={doDelete}
          title="Delete question"
          message="Are you sure you want to delete this MCQ item? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
        />
      )}
      <div className="mcq-viewer__header">
        <h3>{title}</h3>
        {questions.length > 0 &&
          (questions[0].categories || questions[0].category) && (
            <span className="question-category-chip">
              {String(questions[0].categories || questions[0].category)
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase())}
            </span>
          )}
      </div>
      {questions.map((q, index) => {
        const question = q.question || q.title || q.text || "No question text";
        const options = q.options || q.choices || [];
        const correctAnswer = q.answer || q.correct || "";
        const explanation = q.explanation || q?.examples || "";

        return (
          <div
            key={index}
            className={`mcq-question mcq-question--${
              editMode ? "edit" : "view"
            }`}
          >
            {editMode ? (
              <div className="mcq-question__edit">
                <div
                  className={`mcq-textfield mcq-textfield--fullwidth mcq-question__input mcq-question__input--edit`}
                >
                  <label className="mcq-textfield__label">{`Question ${index + 1}`}</label>
                  <textarea
                    className="mcq-textfield__input"
                    rows={3}
                    value={question}
                    onChange={(e) => {
                      const val = e.target.value;
                      setQuestions((prev) =>
                        prev.map((qq, i) =>
                          i === index ? { ...qq, question: val } : qq,
                        ),
                      );
                    }}
                  />
                </div>
                {(options || []).map((option, optIndex) => {
                  let optionText = "";
                  let optionKey = "";

                  if (typeof option === "object" && option !== null) {
                    const keys = Object.keys(option);
                    if (keys.length === 1) {
                      optionKey = keys[0];
                      const firstValue = option[optionKey];
                      optionText =
                        typeof firstValue === "string"
                          ? firstValue
                          : JSON.stringify(firstValue);
                    } else {
                      optionKey = option.key || option.label || "";
                      optionText =
                        option.text ||
                        option.option ||
                        option.value ||
                        option.answer ||
                        JSON.stringify(option);
                    }
                  } else {
                    optionText = option;
                  }

                  const isCorrectIdx =
                    typeof q.correctIndex === "number"
                      ? q.correctIndex
                      : (options || []).findIndex((t) => {
                          if (typeof t === "string") return t === correctAnswer;
                          if (t && typeof t === "object") {
                            const keys = Object.keys(t);
                            if (keys.length === 1) {
                              const key = keys[0];
                              return (
                                key === correctAnswer ||
                                String(t[key]) === correctAnswer
                              );
                            }
                            const tText =
                              t.text ?? t.option ?? t.value ?? t.answer;
                            return String(tText ?? "") === correctAnswer;
                          }
                          return false;
                        });

                  return (
                    <div
                      key={optIndex}
                      className={`mcq-option__edit ${
                        isCorrectIdx === optIndex
                          ? "mcq-option__edit--correct"
                          : ""
                      }`}
                    >
                      <Radio
                        checked={isCorrectIdx === optIndex}
                        onChange={() => {
                          setQuestions((prev) =>
                            prev.map((qq, i) =>
                              i === index
                                ? { ...qq, correctIndex: optIndex }
                                : qq,
                            ),
                          );
                        }}
                        className="mcq-option__radio"
                      />
                      <div className="mcq-option__label">
                        {String.fromCharCode(65 + optIndex)}.
                      </div>
                      <div
                        className={`mcq-textfield mcq-textfield--fullwidth mcq-option__input mcq-option__input--edit`}
                      >
                        <textarea
                          className="mcq-textfield__input"
                          rows={1}
                          value={optionText}
                          onChange={(e) => {
                            const val = e.target.value;
                            setQuestions((prev) =>
                              prev.map((qq, i) => {
                                if (i !== index) return qq;
                                const opts = [...(qq.options || [])];
                                if (
                                  typeof opts[optIndex] === "object" &&
                                  opts[optIndex] !== null
                                ) {
                                  const keys = Object.keys(opts[optIndex]);
                                  if (keys.length === 1) {
                                    const key = keys[0];
                                    opts[optIndex] = { [key]: val };
                                  } else {
                                    if ("text" in opts[optIndex]) {
                                      opts[optIndex] = {
                                        ...opts[optIndex],
                                        text: val,
                                      };
                                    } else if ("option" in opts[optIndex]) {
                                      opts[optIndex] = {
                                        ...opts[optIndex],
                                        option: val,
                                      };
                                    } else if ("value" in opts[optIndex]) {
                                      opts[optIndex] = {
                                        ...opts[optIndex],
                                        value: val,
                                      };
                                    } else if ("answer" in opts[optIndex]) {
                                      opts[optIndex] = {
                                        ...opts[optIndex],
                                        answer: val,
                                      };
                                    } else {
                                      opts[optIndex] = {
                                        ...opts[optIndex],
                                        text: val,
                                      };
                                    }
                                  }
                                } else {
                                  const label = String.fromCharCode(
                                    65 + optIndex,
                                  );
                                  opts[optIndex] = { [label]: val };
                                }
                                return { ...qq, options: opts };
                              }),
                            );
                          }}
                        />
                      </div>
                      <div className="mcq-option__actions">
                        {(() => {
                          const totalOpts = (options || []).length;
                          const disableDelete = totalOpts <= 2;
                          return (
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (disableDelete) return;
                                setQuestions((prev) =>
                                  prev.map((qq, i) => {
                                    if (i !== index) return qq;
                                    const opts = (qq.options || []).filter(
                                      (_, oi) => oi !== optIndex,
                                    );
                                    const nextIdx = Math.min(
                                      qq.correctIndex || 0,
                                      Math.max(0, opts.length - 1),
                                    );
                                    return {
                                      ...qq,
                                      options: opts,
                                      correctIndex: nextIdx,
                                    };
                                  }),
                                );
                              }}
                              className="mcq-option__delete"
                              disabled={disableDelete}
                              title={
                                disableDelete
                                  ? "At least two options are required"
                                  : undefined
                              }
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          );
                        })()}
                        {optIndex === (options || []).length - 1 && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setQuestions((prev) =>
                                prev.map((qq, i) => {
                                  if (i !== index) return qq;
                                  let newOption;
                                  if (qq.options && qq.options.length > 0) {
                                    const firstOption = qq.options[0];
                                    if (
                                      typeof firstOption === "object" &&
                                      firstOption !== null
                                    ) {
                                      const keys = Object.keys(firstOption);
                                      if (keys.length === 1) {
                                        newOption = {
                                          [String.fromCharCode(
                                            65 + qq.options.length,
                                          )]: "",
                                        };
                                      } else {
                                        newOption = {
                                          ...firstOption,
                                          text: "",
                                        };
                                      }
                                    } else {
                                      newOption = {
                                        [String.fromCharCode(
                                          65 + qq.options.length,
                                        )]: "",
                                      };
                                    }
                                  } else {
                                    newOption = {
                                      [String.fromCharCode(
                                        65 + (qq.options || []).length,
                                      )]: "",
                                    };
                                  }
                                  return {
                                    ...qq,
                                    options: [...(qq.options || []), newOption],
                                  };
                                }),
                              );
                            }}
                            className="mcq-option__add"
                          >
                            <AddCircleOutlineIcon />
                          </IconButton>
                        )}
                      </div>
                    </div>
                  );
                })}

                <div
                  className={`mcq-textfield mcq-textfield--fullwidth mcq-question__explanation mcq-question__explanation--edit`}
                >
                  <label className="mcq-textfield__label">Explanation</label>
                  <textarea
                    className="mcq-textfield__input"
                    rows={2}
                    value={explanation}
                    onChange={(e) => {
                      const val = e.target.value;
                      setQuestions((prev) =>
                        prev.map((qq, i) =>
                          i === index ? { ...qq, explanation: val } : qq,
                        ),
                      );
                    }}
                  />
                </div>
              </div>
            ) : (
              <>
                <div
                  className={`mcq-textfield mcq-textfield--fullwidth mcq-question__input mcq-question__input--view mcq-textfield--readonly`}
                >
                  <label className="mcq-textfield__label">{`Question ${index + 1}`}</label>
                  <textarea
                    className="mcq-textfield__input"
                    rows={3}
                    value={question}
                    readOnly={true}
                    onChange={(e) => {
                      const val = e.target.value;
                      setQuestions((prev) =>
                        prev.map((qq, i) =>
                          i === index ? { ...qq, question: val } : qq,
                        ),
                      );
                    }}
                  />
                </div>
                <div className="mcq-options">
                  {options.map((option, optIndex) => {
                    let optionText = "";
                    let optionKey = "";
                    if (typeof option === "object" && option !== null) {
                      const keys = Object.keys(option);
                      if (keys.length === 1) {
                        optionKey = keys[0];
                        const firstValue = option[optionKey];
                        optionText =
                          typeof firstValue === "string"
                            ? firstValue
                            : JSON.stringify(firstValue);
                      } else {
                        optionKey = option.key || option.label || "";
                        optionText =
                          option.text ||
                          option.option ||
                          option.value ||
                          option.answer ||
                          JSON.stringify(option);
                      }
                    } else {
                      optionText = option;
                    }
                    const normalizedCorrect = String(
                      correctAnswer || "",
                    ).trim();
                    const isCorrect =
                      normalizedCorrect !== "" &&
                      (optionText === normalizedCorrect ||
                        optionKey === normalizedCorrect ||
                        (typeof option === "object" &&
                          option !== null &&
                          (option.key === normalizedCorrect ||
                            option.value === normalizedCorrect ||
                            option.text === normalizedCorrect ||
                            option.option === normalizedCorrect ||
                            option.answer === normalizedCorrect)));
                    return (
                      <div
                        key={optIndex}
                        className={`mcq-option ${
                          isCorrect ? "mcq-option--correct" : ""
                        }`}
                      >
                        <span className="mcq-option__label">
                          {String.fromCharCode(65 + optIndex)}.
                        </span>
                        <div
                          className={`mcq-textfield mcq-textfield--fullwidth mcq-option__input mcq-option__input--view mcq-textfield--readonly`}
                        >
                          <textarea
                            className="mcq-textfield__input"
                            rows={1}
                            value={optionText}
                            readOnly={true}
                            onChange={(e) => {
                              const val = e.target.value;
                              setQuestions((prev) =>
                                prev.map((qq, i) => {
                                  if (i !== index) return qq;
                                  const opts = [...(qq.options || [])];
                                  if (
                                    typeof opts[optIndex] === "object" &&
                                    opts[optIndex] !== null
                                  ) {
                                    const keys = Object.keys(opts[optIndex]);
                                    if (keys.length === 1) {
                                      const key = keys[0];
                                      opts[optIndex] = { [key]: val };
                                    } else {
                                      if ("text" in opts[optIndex]) {
                                        opts[optIndex] = {
                                          ...opts[optIndex],
                                          text: val,
                                        };
                                      } else if ("option" in opts[optIndex]) {
                                        opts[optIndex] = {
                                          ...opts[optIndex],
                                          option: val,
                                        };
                                      } else if ("value" in opts[optIndex]) {
                                        opts[optIndex] = {
                                          ...opts[optIndex],
                                          value: val,
                                        };
                                      } else if ("answer" in opts[optIndex]) {
                                        opts[optIndex] = {
                                          ...opts[optIndex],
                                          answer: val,
                                        };
                                      } else {
                                        opts[optIndex] = {
                                          ...opts[optIndex],
                                          text: val,
                                        };
                                      }
                                    }
                                  } else {
                                    opts[optIndex] = val;
                                  }
                                  return { ...qq, options: opts };
                                }),
                              );
                            }}
                          />
                        </div>
                        {isCorrect && (
                          <span className="mcq-option__indicator">✓</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {explanation && !editMode && (
                  <div className="mcq-explanation">
                    <strong>Explanation:</strong> {explanation}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
      {permission && (
        <div className="mcq-viewer__actions">
          {(pageId || allowEditWithoutPersist) &&
            (editMode ? (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleCloseMCQ()}
                  className="mcq-viewer__action-button"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={pageId ? doUpdate : () => setEditMode(false)}
                  className="mcq-viewer__action-button"
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setEditMode(true)}
                  className="mcq-viewer__action-button"
                >
                  <EditIcon className="toolbar-button-icon" />
                  Edit
                </Button>
                {pageId && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setIsDeleteConfirmOpen(true)}
                    className="mcq-viewer__action-button"
                  >
                    <DeleteIcon className="toolbar-button-icon" />
                    Delete
                  </Button>
                )}
                {onAddNext && pageId && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={onAddNext}
                    className="mcq-viewer__action-button"
                  >
                    <AddIcon className="toolbar-button-icon" />
                    Add Next
                  </Button>
                )}
              </>
            ))}
        </div>
      )}
    </div>
  );
};

export default MCQViewer;
